import { RootState, SetState } from '@/store/useStore.ts';
import {
  UserContext,
  UserContextSlice,
  UserStatus,
  UserStoreParts,
} from '@/store/slices/userContext/types';
import { emptySliceError } from '../sliceError';

const defaultUserContext: UserContext = {
  data: null,
};

const defaultUserStatus: UserStatus = {
  loading: false,
};

const defaults = {
  [UserStoreParts.userContext]: defaultUserContext,
  [UserStoreParts.userStatus]: defaultUserStatus,
  [UserStoreParts.userError]: emptySliceError(),
} as const;

export const userContextSlice = ({
  set,
}: SetState<RootState>): UserContextSlice => ({
  userContext: { ...defaults[UserStoreParts.userContext] },
  userContextStatus: { ...defaults[UserStoreParts.userStatus] },
  userContextError: { ...defaults[UserStoreParts.userError] },

  setUserContext: (newState) =>
    set(
      (state: RootState) => ({
        ...state,
        userContext: { ...state.userContext, ...newState },
      }),
      false,
      'userContext/setUserContext',
    ),

  setUserContextStatus: (newState) =>
    set(
      (state: RootState) => ({
        ...state,
        userContextStatus: { ...state.userContextStatus, ...newState },
      }),
      false,
      'userContext/setUserContextStatus',
    ),

  setUserContextError: (newState) =>
    set(
      (state: RootState) => ({
        ...state,
        userContextError: {
          ...state.userContextError,
          ...newState,
        },
      }),
      false,
      'userContext/setUserError',
    ),

  resetUserContextPart: (part: UserStoreParts) =>
    set(
      (state) => {
        const patch: Partial<RootState> = {};
        if (part === UserStoreParts.userContext) {
          patch.userContext = { ...defaults[UserStoreParts.userContext] };
        } else if (part === UserStoreParts.userStatus) {
          patch.userContextStatus = { ...defaults[UserStoreParts.userStatus] };
        } else if (part === UserStoreParts.userError) {
          patch.userContextError = { ...defaults[UserStoreParts.userError] };
        }
        return { ...state, ...patch };
      },
      false,
      `userContext/reset/${part}`,
    ),

  resetAllUserContextParts: () =>
    set(
      () => ({
        userContext: { ...defaults[UserStoreParts.userContext] },
        userContextStatus: { ...defaults[UserStoreParts.userStatus] },
        userContextError: { ...defaults[UserStoreParts.userError] },
      }),
      false,
      'userContext/resetAll',
    ),
});

export type {
  UserAccountSettings,
  UserContextData,
  UserContextSlice,
} from './types';
export { UserStoreParts } from './types';
